import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowRight, Tag } from "lucide-react";
import Seo from "../components/Seo";
import { blogPosts } from "../data/blogPosts";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: "easeOut" },
  }),
};

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Blog",
  name: "Yoga Retreats Booking Blog",
  url: "https://yogaretreatsbooking.com/blog",
  description:
    "Expert guides on yoga retreats in India, meditation tips, beginner yoga advice, and wellness travel inspiration.",
  publisher: {
    "@type": "Organization",
    name: "Yoga Retreats Booking",
    url: "https://yogaretreatsbooking.com",
  },
};

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function BlogCard({ post, index }) {
  return (
    <motion.article
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeUp}
      custom={index}
      className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 border border-gray-100 flex flex-col"
    >
      <Link
        to={`/blog/${post.slug}`}
        className="block overflow-hidden"
        aria-label={post.title}
      >
        <img
          src={post.coverImage}
          alt={post.title}
          className="w-full h-52 object-cover hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </Link>
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center gap-3 mb-3">
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
            <Tag size={11} />
            {post.category}
          </span>
          <span className="text-xs text-gray-400">{formatDate(post.date)}</span>
        </div>
        <Link to={`/blog/${post.slug}`}>
          <h2 className="text-lg font-bold text-gray-900 mb-2 leading-snug hover:text-emerald-700 transition-colors line-clamp-2">
            {post.title}
          </h2>
        </Link>
        <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1 line-clamp-3">
          {post.excerpt}
        </p>
        <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
          <span className="flex items-center gap-1.5 text-xs text-gray-400">
            <Clock size={13} />
            {post.readTime}
          </span>
          <Link
            to={`/blog/${post.slug}`}
            className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 hover:text-emerald-700 transition-colors"
          >
            Read More
            <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </motion.article>
  );
}

function Blog() {
  return (
    <div className="min-h-screen bg-gray-50/50">
      <Seo
        title="Blog — Yoga Retreat Guides & Wellness Tips"
        description="Explore expert guides on yoga retreats in India, meditation benefits, beginner yoga tips, and packing checklists for your next wellness journey in Rishikesh or Goa."
        canonicalPath="/blog"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative pt-16 overflow-hidden bg-gradient-to-br from-emerald-800 via-teal-700 to-emerald-600">
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1400&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/30 text-emerald-100 text-sm font-medium mb-4 border border-emerald-400/40">
              Insights & Inspiration
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-5">
              Our <span className="text-emerald-300">Blog</span>
            </h1>
            <p className="text-emerald-100/90 text-lg leading-relaxed">
              Expert guides, wellness tips, and travel inspiration for your yoga and meditation journey in India.
            </p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gray-50/50" style={{ clipPath: "ellipse(55% 100% at 50% 100%)" }} />
      </section>

      {/* Blog grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-8">
          {blogPosts.map((post, index) => (
            <BlogCard key={post.slug} post={post} index={index} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white border-t border-gray-100 py-16">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Ready to Begin Your Retreat?</h2>
          <p className="text-gray-500 mb-7">
            Browse our curated collection of yoga and meditation retreats across India.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors shadow-sm"
            >
              Browse Retreats
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold hover:border-emerald-300 hover:text-emerald-700 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Blog;
